// src/pages/FirstRunTourPage.tsx
//
// First-run tour: shown ONCE per account right after a talent is
// logged in AND Stripe is complete. Not a second sales pitch — each screen
// points at a REAL screen in the app (Tier Level sheet, QR pass, Balance
// card + Earned-this-month chart, Profile-visits card, Leaderboard) with the
// value line as its caption. Same chrome as the native welcome tour
// (WelcomeTourPage): black, small wordmark, staggered rise-ins, green
// full-width Continue, dots. No Skip — it's six short screens (founder decision).
//
// Gate: components/FirstRunTourGate (mounted in RouteGuards) routes an
// account here once — logged in, Stripe complete, User.firstRunTourSeenAt
// unset (no backfill: pre-existing talents see it once too). "Get Started"
// stamps the flag (fire-and-forget, src/lib/firstRunTour.ts), then:
//   • account still on its registration placeholder price → the price step
//     rides INSIDE the tour chrome (same PATCH /users/:slug/price as the
//     Phase C popup and the dashboard editor; no dots, button "Save price"),
//   • otherwise → straight to /requests, where the DEMO card lives.
// QA params: ?slide=N opens on screen N (0-based); ?price=1 forces the price
// step on (it saves the real price — harmless, it's the talent's own editor).
import React, { useContext, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import QRCode from "react-qr-code";
import { usersApi } from "../services/users";
import { ordersApi } from "../services/orders";
import { launchOfferEnds } from "../components/FeeTierOffer";
import { referralShareText } from "../components/ReferEarnSheet";
import { copyTextToClipboard } from "../lib/clipboard";
import { AuthContext } from "../context/AuthContext";
import NumberPad from "../components/NumberPad";
import PriceFloorNote from "../components/PriceFloorNote";
import { belowFloor, liftToFloor } from "../utils/priceFloor";
import { CURRENCY_NAMES, SUFFIX_SYMBOL_CURRENCIES, ProfileBits } from "../components/PriceSetupModal";
import { currencySymbol, formatAmountForCopy } from "../utils/currency";
import { chipsFor, defaultPriceFor, minRequestPriceFor, maxPriceFor, needsPriceSetup, validatePrice } from "../utils/payinConfig";
import { markFirstRunTourSeen } from "../lib/firstRunTour";
import { SHOW_TIERS } from "../lib/tiersPaused";
import { buildPassDesign, qrSvgToPngDataUrl } from "../utils/walletPassRenderer";
import "../components/PriceSetupModal.css";
import "./FirstRunTourPage.css";
import LeaderboardFlame from "../components/LeaderboardFlame";
import { t as tr } from "../i18n";
import { T } from "../i18n/T";
import { WORLD_LAND_STRIP } from "../components/globe/worldLandStrip";

// ── tiny animation hooks ──────────────────────────────────────────────────
// Count from → to with an ease-out, starting `delay` ms after `active`.
function useCountUp(active: boolean, from: number, to: number, dur: number, delay: number, run: number, steps = 0) {
  const [v, setV] = useState(from);
  useEffect(() => {
    if (!active) return;
    setV(from);
    let raf = 0;
    const t = window.setTimeout(() => {
      const t0 = performance.now();
      const step = (n: number) => {
        let p = Math.min(1, (n - t0) / dur);
        if (steps > 0) p = Math.ceil(p * steps) / steps; // coarse: only `steps` distinct numbers show
        const e = 1 - Math.pow(1 - p, 3);
        setV(from + (to - from) * e);
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);
    return () => {
      window.clearTimeout(t);
      cancelAnimationFrame(raf);
    };
  }, [active, from, to, dur, delay, run, steps]);
  return v;
}

// Step through fixed stops: [[value, atMs], ...]
function useSteps<T>(active: boolean, stops: readonly (readonly [T, number])[], run: number) {
  const [v, setV] = useState<T>(stops[0][0]);
  useEffect(() => {
    if (!active) return;
    setV(stops[0][0]);
    const ts = stops.map(([val, at]) => window.setTimeout(() => setV(val), at));
    return () => ts.forEach((t) => window.clearTimeout(t));
  }, [active, stops, run]);
  return v;
}

// Cycle 0..n-1 every `every` ms, starting after `delay`; resets on entry.
function useCycle(active: boolean, n: number, every: number, delay: number, run: number) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!active) return;
    setI(0);
    let iv: number | undefined;
    const t = window.setTimeout(() => {
      iv = window.setInterval(() => setI((k) => (k + 1) % n), every);
    }, delay);
    return () => {
      window.clearTimeout(t);
      if (iv !== undefined) window.clearInterval(iv);
    };
  }, [active, n, every, delay, run]);
  return i;
}

const whole = (n: number) => "$" + Math.round(n).toLocaleString("en-US");
const int = (n: number) => Math.round(n).toLocaleString("en-US");

// ── Tier Level medal (FeeTierSheet's V2 medal, tinted per tier) ───────────
const TIER_FILL: Record<string, [string, string]> = {
  silver: ["#C9CDD4", "#8b93a3"],
  platinum: ["#E8EDF2", "#9aa7b3"],
  legend: ["#24C961", "#127a3c"],
};
const Medal: React.FC<{ tier: string; className?: string }> = ({ tier, className }) => {
  const [fill, star] = TIER_FILL[tier];
  return (
    <svg className={className} width="54" height="60" viewBox="0 0 54 60" aria-hidden="true">
      <path d="M17 2h20l-6 16H23z" fill="#3a4150" />
      <path d="M13 2h10l-5 14z" fill="#4a5262" />
      <path d="M41 2H31l5 14z" fill="#2e3542" />
      <circle cx="27" cy="36" r="19" fill={fill} />
      <circle cx="27" cy="36" r="19" fill="url(#frt-sheen)" />
      <circle cx="27" cy="36" r="13.5" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1.5" />
      <path d="M27 27l2.6 5.4 6 .8-4.4 4.1 1.1 5.9-5.3-2.9-5.3 2.9 1.1-5.9-4.4-4.1 6-.8z" fill={star} />
    </svg>
  );
};

// Journey stops: Silver (open) → Platinum exactly when the bar (linear,
// 1.4s→3.6s) crosses halfway → Legend as it lands (founder r3).
const TIER_STOPS = [
  ["silver", 0],
  ["platinum", 2500],
  ["legend", 3600],
] as const;
const PV_STOPS = [
  ["profile", 0],
  ["playlist", 1500],
] as const;
const TIER_META: Record<string, { name: string; keep: number; next: React.ReactNode }> = {
  silver: { name: "SILVER", keep: 91, next: <><T k="firstRunTourPage.nextUpPlatinum" tags={{ 0: (c) => <b>{c}</b> }} /></> },
  platinum: { name: "PLATINUM", keep: 93, next: <><T k="firstRunTourPage.nextUpLegend" tags={{ 0: (c) => <b>{c}</b> }} /></> },
  legend: { name: "LEGEND", keep: 95, next: <><T k="firstRunTourPage.lockedInForever" tags={{ 0: (c) => <b>{c}</b> }} /></> },
};

// ── Pass colours for the QR slide (founder r4: green → pink → blue → bright
//    yellow, fast). Pink/yellow go through the renderer's custom-colour path,
//    exactly like a talent picking them on the colour wheel. ──────────────
const PASS_COLORS = [
  { key: "green", swatch: "#24C961", opts: { color: "green" as const } },
  { key: "pink", swatch: "#FF3D9A", opts: { color: "custom" as const, customColor: "#FF3D9A" } },
  { key: "blue", swatch: "#2563EB", opts: { color: "blue" as const } },
  { key: "yellow", swatch: "#FFE01B", opts: { color: "custom" as const, customColor: "#FFE01B" } },
];

// ── Leaderboard data — LIVE snapshots from production so the tour
//    reads true; only the fan $ amounts are inflated (founder). Faces: the
//    roster photos already bundled; fans use the pink letter avatar the real
//    Top Fans board falls back to. ────────────────────────────────────────
type Row = { name: string; sub: string; val: string; img?: string; letter?: boolean };
const DJS: Row[] = [
  // Founder's order — roster faces, mock counts; two-word locations.
  { name: "DJ Lucky", sub: "Paris, France", val: "318", img: "/RequestDJImages/djlucky.jpg" },
  { name: "Xoul", sub: "Seattle, Washington", val: "276", img: "/RequestDJImages/djxoul.jpg" },
  { name: "Lucha Libre", sub: "San Juan, Puerto Rico", val: "241", img: "/RequestDJImages/djluchalibre.jpg" },
  { name: "Frisco", sub: "Toronto, Canada", val: "198", img: "/RequestDJImages/djfrisco.jpg" },
  { name: "DJ Benofficial", sub: "Los Angeles, California", val: "173", img: "/RequestDJImages/djbenofficial.jpg" },
];
// Live /orders/trending (all-time) snapshot — the "Setlist" bucket
// (playlists) is excluded on purpose: playlists don't belong on the song
// board (founder). Covers are the same Spotify CDN art the real board shows.
const SONGS: Row[] = [
  { name: "Clarity", sub: "Zedd, Foxes", val: "55", img: "https://i.scdn.co/image/ab67616d0000b273941dd3b3343d9cb9329d37bf" },
  { name: "Tití Me Preguntó", sub: "Bad Bunny", val: "54", img: "https://i.scdn.co/image/ab67616d0000b27349d694203245f241a1bcaa72" },
  { name: "Lose Control", sub: "Teddy Swims", val: "52", img: "https://i.scdn.co/image/ab67616d0000b2739a7e719f37e9264656b95997" },
  { name: "Stateside", sub: "PinkPantheress, Zara Larsson", val: "51", img: "https://i.scdn.co/image/ab67616d0000b2738c2de5de3e20fa408171f517" },
  { name: "Mr. Brightside", sub: "The Killers", val: "42", img: "https://i.scdn.co/image/ab67616d0000b273ccdddd46119a4ff53eaf1f5d" },
];
const FANS: Row[] = [
  { name: "Jmoloney232", sub: tr("firstRunTourPage.sampleRequests", { count: 10 }), val: "$485", letter: true },
  { name: "Divy", sub: tr("firstRunTourPage.sampleRequests", { count: 7 }), val: "$310", letter: true },
  { name: "jonathan", sub: tr("firstRunTourPage.sampleRequests", { count: 13 }), val: "$225", letter: true },
  { name: "Raghu", sub: tr("firstRunTourPage.sampleRequests", { count: 7 }), val: "$180", letter: true },
  { name: "Brandon", sub: tr("firstRunTourPage.sampleRequests", { count: 16 }), val: "$140", letter: true },
];
const LB_VIEWS = [tr("firstRunTourPage.topSongs"), tr("firstRunTourPage.topTalent"), tr("firstRunTourPage.topFansTab")] as const;

// ── Earned-this-month card (MonthlyEarningsCard replica, static data) ────
// Cumulative day-by-day lines, this month (green) vs last month (dotted).
const CHART_W = 300;
const CHART_H = 92;
const THIS_MONTH = [0, 40, 40, 95, 150, 150, 210, 300, 300, 340, 420, 480, 480, 560, 640, 640, 700, 790, 850, 850, 930, 1010, 1010, 1080, 1160, 1220, 1220, 1284];
const LAST_MONTH = [0, 20, 60, 60, 110, 150, 150, 200, 260, 260, 300, 350, 350, 410, 450, 450, 520, 560, 560, 620, 660, 660, 720, 760, 760, 820, 860, 860, 910, 940, 972];
function linePath(pts: number[], maxY: number, days: number) {
  const span = days - 1;
  return pts
    .map((v, i) => `${i === 0 ? "M" : "L"}${(2 + (i / span) * (CHART_W - 4)).toFixed(1)},${(CHART_H - (v / maxY) * CHART_H).toFixed(1)}`)
    .join(" ");
}
const CHART_MAX = 1284 * 1.15;
const THIS_PATH = linePath(THIS_MONTH, CHART_MAX, 31);
const LAST_PATH = linePath(LAST_MONTH, CHART_MAX, 31);
const HEAD_X = ((2 + ((THIS_MONTH.length - 1) / 30) * (CHART_W - 4)) / CHART_W) * 100;
const HEAD_Y = ((CHART_H - (1284 / CHART_MAX) * CHART_H) / CHART_H) * 100;

type SlideKey = "tiers" | "qr" | "cashout" | "visits" | "leaderboard" | "refer" | "fivek" | "price";

// Order (founder r7): Cash Out after the Leaderboard. The price step is
// appended at runtime only for accounts that still need one.
// While Tier Level is paused (founder 2026-10-22, lib/tiersPaused) the tiers
// slide — "You keep 90%", the road to Legend 95% — leaves the tour, so it
// opens on "Your Link, Your Stage" with one dot fewer.
const withoutPausedTiers = (tour: SlideKey[]): SlideKey[] => (SHOW_TIERS ? tour : tour.filter((k) => k !== "tiers"));
const BASE_TOUR: SlideKey[] = withoutPausedTiers(["tiers", "qr", "visits", "leaderboard", "cashout", "fivek"]);
// Refer & Earn slide (founder pick 5B) sits right before the finale — only for
// talent the program is offered to (usersApi.referrals → eligible).
const TOUR_WITH_REFER: SlideKey[] = withoutPausedTiers(["tiers", "qr", "visits", "leaderboard", "cashout", "refer", "fivek"]);

const EyeIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

// Glass globe (CSS sphere + real continents — Natural Earth 110m land on an
// orthographic projection, Americas facing) with a stack of bills — the $5k
// screen's visual. No emoji; brand green.
// The land strip (WORLD_LAND_STRIP, components/globe) is drawn twice and
// scrolled one width under a circular clip = one full rotation, then it rests
// (founder r6).
const GlobeCash = () => (
  <div className="frt-globe-wrap">
    <div className="frt-orb">
      <svg className="frt-orb-lines" viewBox="0 0 190 190" aria-hidden="true">
        <defs>
          <clipPath id="frt-orb-clip">
            <circle cx="95" cy="95" r="95" />
          </clipPath>
        </defs>
        <g clipPath="url(#frt-orb-clip)">
          <g className="frt-orb-map">
            <path className="frt-orb-land" d={WORLD_LAND_STRIP} />
            <path className="frt-orb-land" d={WORLD_LAND_STRIP} transform="translate(380 0)" />
          </g>
        </g>
      </svg>
      <span className="frt-orb-shine" />
    </div>
    <svg className="frt-bills" viewBox="0 0 110 78" width="110" height="78" aria-hidden="true">
      <defs>
        <linearGradient id="frt-bill" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#4ade80" />
          <stop offset="1" stopColor="#16a34a" />
        </linearGradient>
      </defs>
      <g transform="translate(6 8) rotate(-9 48 30)">
        <g className="frt-bill frt-bill--3"><rect x="0" y="26" width="96" height="38" rx="7" fill="#0b4d24" stroke="#1da550" strokeWidth="2" /></g>
        <g className="frt-bill frt-bill--2"><rect x="0" y="13" width="96" height="38" rx="7" fill="#127a3c" stroke="#24C961" strokeWidth="2" /></g>
        <g className="frt-bill frt-bill--1">
          <rect x="0" y="0" width="96" height="38" rx="7" fill="url(#frt-bill)" stroke="#86efac" strokeWidth="2" />
          <circle cx="48" cy="19" r="11" fill="none" stroke="#052e16" strokeWidth="2" />
          <text x="48" y="24" textAnchor="middle" fontSize="15" fontWeight="800" fill="#052e16" fontFamily="inherit">$</text>
          <rect x="9" y="8" width="10" height="22" rx="2" fill="none" stroke="#052e16" strokeWidth="1.4" opacity="0.7" />
          <rect x="77" y="8" width="10" height="22" rx="2" fill="none" stroke="#052e16" strokeWidth="1.4" opacity="0.7" />
        </g>
      </g>
    </svg>
  </div>
);

export default function FirstRunTourPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading } = useContext(AuthContext);
  const [params] = useSearchParams();
  const startAt = Math.max(0, parseInt(params.get("slide") || "0", 10) || 0);
  const forcePriceStep = params.get("price") === "1";

  // The account's price state, read once while the screens play. Decides
  // whether the price step is appended; a failed read just skips it —
  // PriceSetupModal on the Requests page is the catch-all for a price that's
  // still pending (the flag is server-side, so nothing is lost).
  const [profile, setProfile] = useState<ProfileBits | null>(null);
  useEffect(() => {
    let alive = true;
    if (!user?.slug) return;
    usersApi
      .get(user.slug)
      .then((data) => {
        if (!alive) return;
        setProfile(data);
        setPrice(
          typeof data.pricePerRequest === "number" && data.pricePerRequest > 0
            ? data.pricePerRequest
            : defaultPriceFor(data.currency)
        );
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [user?.slug]);
  const priceStep = !!profile && (forcePriceStep || needsPriceSetup(profile));
  // Their real code for the Refer & Earn slide. Not offered / older server /
  // failed fetch = null and the tour is exactly today's six slides.
  const [referCode, setReferCode] = useState<string | null>(null);
  const idxRef = useRef(0);
  useEffect(() => {
    if (!user?.slug) return;
    let alive = true;
    usersApi
      .referrals()
      // Adding the slide shifts the finale one step right — only safe while
      // they are still before it (the answer normally lands on slide one).
      .then((r) => { if (alive && r?.eligible && r.code && idxRef.current < BASE_TOUR.indexOf("fivek")) setReferCode(r.code); })
      .catch(() => {});
    return () => { alive = false; };
  }, [user?.slug]);
  // The launch offer (1.5.1 #12): its talent keep 100% for their first 3
  // months, so the first screen must not say "You keep 90% … From day one".
  // No offer, an older server or a failed read = today's line. Only the tiers
  // slide reads it, so no read while that slide is paused.
  const [launchOffer, setLaunchOffer] = useState(false);
  useEffect(() => {
    if (!user?.slug || !SHOW_TIERS) return;
    let alive = true;
    ordersApi
      .feeTier()
      .then((s) => { if (alive) setLaunchOffer(!!launchOfferEnds(s)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [user?.slug]);
  const TOUR = referCode ? TOUR_WITH_REFER : BASE_TOUR;
  const slides: SlideKey[] = priceStep ? [...TOUR, "price"] : TOUR;

  const [idx, setIdx] = useState(Math.min(startAt, BASE_TOUR.length - 1));
  idxRef.current = idx;

  // ── price step state (mirrors PriceSetupModal, same validation + PATCH) ──
  const [price, setPrice] = useState<number>(0);
  const [padOpen, setPadOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [priceError, setPriceError] = useState("");
  const currency = String(profile?.currency || "usd").toLowerCase();
  const sym = currencySymbol(currency);
  const suffix = SUFFIX_SYMBOL_CURRENCIES.has(currency);
  const chips = chipsFor(currency);
  const priceCheck = validatePrice(padOpen && draft !== "" ? Number(draft) : price, currency, profile?.capsLifted, profile);
  const openPad = () => {
    setDraft("");
    setPadOpen(true);
  };
  const closePad = () => {
    const n = parseInt(draft, 10);
    if (draft.trim() !== "" && !isNaN(n)) {
      // Under the floor ("Minimum $2" on the pad) → the floor.
      const v = validatePrice(liftToFloor(n, currency, profile), currency, profile?.capsLifted, profile);
      if (v.ok && typeof v.price === "number" && v.price > 0) setPrice(v.price);
    }
    setDraft("");
    setPadOpen(false);
  };
  const selectChip = (p: number) => {
    setPrice(p);
    setDraft("");
    setPadOpen(false);
  };
  const savePrice = async () => {
    if (saving || !profile) return;
    const v = validatePrice(price, currency, profile.capsLifted, profile);
    if (!v.ok || typeof v.price !== "number" || v.price <= 0) {
      setPriceError(
        tr("firstRunTourPage.enterAPriceBetween", { amount: formatAmountForCopy(minRequestPriceFor(currency, profile), currency), amount2: formatAmountForCopy(maxPriceFor(currency, profile.capsLifted), currency) })
      );
      return;
    }
    setSaving(true);
    setPriceError("");
    try {
      await usersApi.setPrice(profile.slug, v.price);
      // The save cleared pricePendingSetup server-side — Requests renders the
      // real price on its own profile read, no reload needed.
      navigate("/requests", { replace: true });
    } catch {
      setPriceError(tr("firstRunTourPage.couldnTSaveCheck"));
      setSaving(false);
    }
  };
  // Bumps on every slide entry so per-slide animations replay (same trick as
  // the welcome tour's requestsRun/payoutRun).
  const [run, setRun] = useState(0);
  useEffect(() => {
    setRun((n) => n + 1);
  }, [idx]);
  const cur = slides[idx];

  // Slide 1 — tier journey
  const tier = useSteps(cur === "tiers", TIER_STOPS, run);

  // Slide 2 — the REAL pass renderer (walletPassRenderer.buildPassDesign),
  // one design per tour colour, pre-rendered once; the slide crossfades
  // through them and the swatch row follows. Identical pixels to /qr.
  const qrSvgRef = useRef<HTMLDivElement | null>(null);
  const [passImgs, setPassImgs] = useState<Record<string, string>>({});
  useEffect(() => {
    let cancelled = false;
    const t = window.setTimeout(async () => {
      const svgEl = qrSvgRef.current?.querySelector("svg");
      if (!svgEl) return;
      const qrPng = await qrSvgToPngDataUrl(svgEl as SVGSVGElement, 600);
      const out: Record<string, string> = {};
      for (const c of PASS_COLORS) {
        const canvas = await buildPassDesign({ ...c.opts, djName: "DJ Lucky", qrImage: qrPng, rLogo: "/r.png", width: 900 });
        out[c.key] = canvas.toDataURL("image/png");
        if (cancelled) return;
      }
      if (!cancelled) setPassImgs(out);
    }, 50);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, []);
  const passIdx = useCycle(cur === "qr", PASS_COLORS.length, 1500, 1600, run);
  const passKey = PASS_COLORS[passIdx].key;

  // Slide 3 — balance count-up (whole dollars — founder r4)
  const balance = useCountUp(cur === "cashout", 0, 1284, 900, 600, run, 8);

  // Slide 4 — the visits card flips green → purple in place (founder r4);
  // Profile visits → Storefront visits (the Sell Playlists storefront), quick (r5)
  const pvPhase = useSteps(cur === "visits", PV_STOPS, run);
  const pvProfile = useCountUp(cur === "visits", 0, 4930, 700, 400, run, 6);
  const pvPlaylist = useCountUp(cur === "visits" && pvPhase === "playlist", 0, 2184, 700, 100, run, 6);

  // Slide 5 — leaderboard cycles Songs → DJs → Fans
  const lbView = useCycle(cur === "leaderboard", LB_VIEWS.length, 1800, 1400, run);

  const onPriceStep = cur === "price";
  const lastScreen = idx === TOUR.length - 1;
  const next = () => {
    if (onPriceStep) {
      // The pad sheet covers the footer, but keep the rule explicit: a typed
      // draft commits through Done, never through Save.
      if (padOpen) return;
      savePrice();
      return;
    }
    if (!lastScreen) {
      setIdx(idx + 1);
      return;
    }
    // "Get Started": the tour is behind this account from here on — even if
    // the app dies during the price step, PriceSetupModal picks the price up.
    if (user?.slug) markFirstRunTourSeen(user.slug);
    if (priceStep) setIdx(idx + 1);
    else navigate("/requests", { replace: true });
  };

  const cls = (k: SlideKey) => `wt-slide frt-slide frt-slide--${k}${k === cur ? " wt-slide--active" : ""}`;

  const rows = (list: Row[]) =>
    list.map((r, i) => (
      <div className="frt-board-row" key={r.name}>
        {/* The real board's ranks (build 44): gold / silver / bronze numbers, no medal emoji. */}
        <span className={`frt-rk${i < 3 ? ` frt-rk--${i + 1}` : ""}`}>{i + 1}</span>
        {r.letter ? (
          <span className="frt-av frt-av--letter" aria-hidden>
            {Array.from(r.name.trim())[0]?.toUpperCase()}
          </span>
        ) : (
          <img className={`frt-av${list === SONGS ? " frt-av--sq" : ""}`} src={r.img} alt="" referrerPolicy="no-referrer" />
        )}
        <span className="frt-nm">
          {r.name}
          <small>{r.sub}</small>
        </span>
        <span className="frt-ct">{r.val}</span>
      </div>
    ));

  const lbList = lbView === 0 ? SONGS : lbView === 1 ? DJS : FANS;

  // Signed-out (a stale deep link, an expired session): the tour has nothing
  // to stamp — send them to log in; the gate brings them back here if due.
  if (!loading && !isAuthenticated) return <Navigate to="/login" replace />;

  return (
    <div className="wt-page frt-page frt-page--gradient">
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <defs>
          <linearGradient id="frt-sheen" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#fff" stopOpacity="0.55" />
            <stop offset="0.5" stopColor="#fff" stopOpacity="0" />
            <stop offset="1" stopColor="#000" stopOpacity="0.18" />
          </linearGradient>
        </defs>
      </svg>
      {/* Hidden QR source for the pass renderer — zero-size clipped box, same
          reasoning as DJQrPage (an invisible 600px box would widen the page). */}
      <div
        ref={qrSvgRef}
        aria-hidden
        style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", opacity: 0, pointerEvents: "none" }}
      >
        <QRCode value="https://requesta.app/djlucky" size={600} level="H" />
      </div>

      <div className="wt-viewport">
        <div
          className="wt-track"
          style={{ width: `${slides.length * 100}%`, transform: `translateX(-${(idx * 100) / slides.length}%)` }}
        >
          {slides.map((k) => (
            <section key={k} className={cls(k)} style={{ width: `${100 / slides.length}%` }} aria-hidden={k !== cur}>
              <img src="/request-wordmark.png" alt="" className="wt-logo-sm wt-in wt-d1" />

              {k === "tiers" && (
                <>
                  <h2 className="wt-heading wt-in wt-d2">
                    <T
                      k={launchOffer ? "firstRunTourPage.youKeepAllFirstMonths" : "firstRunTourPage.youKeepOfEvery"}
                      tags={{ 0: (c) => <span className="heading-accent">{c}</span>, 1: <br />, 2: (c) => <span className="frt-heading-sub">{c}</span> }}
                    />
                  </h2>
                  <div className="frt-mid">
                    <div key={run} className={`frt-tier wt-in wt-d4${k === cur ? " frt-j" : ""}`}>
                      <div className="frt-medal-wrap">
                        <Medal tier="silver" className="frt-st frt-st--1" />
                        <Medal tier="platinum" className="frt-st frt-st--2" />
                        <Medal tier="legend" className="frt-st frt-st--3" />
                      </div>
                      <div className="frt-tier-name" style={{ color: TIER_FILL[tier][0] }}>{TIER_META[tier].name}</div>
                      <div className="frt-hero">{TIER_META[tier].keep}%</div>
                      <div className="frt-hero-sub">{tr("firstRunTourPage.ofEveryRequestIs")}</div>
                      <div className="frt-bar" aria-hidden><i /></div>
                      <div className="frt-next">{TIER_META[tier].next}</div>
                      <div className="frt-road" aria-hidden>
                        <span className={`frt-dot${tier === "silver" ? " frt-dot--cur" : ""}`} style={{ background: TIER_FILL.silver[0] }} />
                        <span className={`frt-dot${tier === "platinum" ? " frt-dot--cur" : tier === "silver" ? " frt-dot--todo" : ""}`} style={{ background: TIER_FILL.platinum[0] }} />
                        <span className={`frt-dot${tier === "legend" ? " frt-dot--cur" : " frt-dot--todo"}`} style={{ background: TIER_FILL.legend[0] }} />
                      </div>
                      <div className="frt-floor">{tr("firstRunTourPage.legendKeepsForever")}</div>
                    </div>
                  </div>
                  <p className="frt-caption wt-in wt-d5">
                    <T k="firstRunTourPage.workYourWayTo" tags={{ 0: (c) => <span className="heading-accent">{c}</span> }} />
                  </p>
                </>
              )}

              {k === "qr" && (
                <>
                  <h2 className="wt-heading wt-in wt-d2">
                    <T k="firstRunTourPage.yourLinkYourStage" tags={{ 0: (c) => <span className="heading-accent">{c}</span> }} />
                  </h2>
                  <div className="frt-mid">
                    {/* /qr replica: the pass preview frame + "Choose a color" swatches */}
                    <div className="frt-passframe wt-in wt-d3">
                      {PASS_COLORS.map((c) =>
                        passImgs[c.key] ? (
                          <img key={c.key} src={passImgs[c.key]} alt="" className={`frt-passimg${c.key === passKey ? " frt-passimg--on" : ""}`} />
                        ) : null
                      )}
                    </div>
                    <div className="frt-colorpicker wt-in wt-d4" aria-hidden>
                      <p className="frt-color-label">{tr("firstRunTourPage.chooseAColor")}</p>
                      <div className="frt-swatches">
                        {PASS_COLORS.map((c) => (
                          <span key={c.key} className={`frt-swatch${c.key === passKey ? " frt-swatch--on" : ""}`} style={{ background: c.swatch }} />
                        ))}
                        <span className="frt-swatch frt-swatch--wheel" />
                      </div>
                    </div>
                  </div>
                  <p className="frt-caption wt-in wt-d5">
                    <T k="firstRunTourPage.addToAppleWallet" tags={{ 0: (c) => <span className="heading-accent">{c}</span>, 1: (c) => <span className="heading-accent">{c}</span> }} />
                  </p>
                </>
              )}

              {k === "cashout" && (
                <>
                  <h2 className="wt-heading wt-in wt-d2">
                    <T k="firstRunTourPage.cashOutStraightTo" tags={{ 0: <br />, 1: (c) => <span className="heading-accent">{c}</span> }} />
                  </h2>
                  <p className="wt-sub-line frt-sub-strong wt-in wt-d3">{tr("firstRunTourPage.morePayoutOptions")}</p>
                  <div className="frt-mid">
                    <div className="frt-balance wt-in wt-d4">
                      <img src="/request-wordmark.png" alt="" className="frt-balance-logo" />
                      <div className="frt-balance-block">
                        <div className="frt-balance-label">{tr("firstRunTourPage.currentBalance")}</div>
                        <div className="frt-balance-amount">{whole(balance)}</div>
                      </div>
                      <div className="frt-balance-foot">
                        <span className="frt-balance-name">{tr("firstRunTourPage.djLucky")}</span>
                        <span className="frt-balance-bank">{tr("firstRunTourPage.chase")}</span>
                      </div>
                    </div>
                    {/* TODO(MassPay live): restore the payout-rail row here —
                        Bank · Wallet · Crypto chips (.frt-rails / .frt-rail in
                        the CSS, Bank selected). Pulled 9/03 because Wallet and
                        Crypto rails aren't available at launch (founder). */}
                    {/* Earned-this-month chart — the App Store "Watch your
                        earnings grow" card (MonthlyEarningsCard replica). */}
                    <div key={run} className="frt-mec wt-in wt-d5" aria-hidden>
                      <div className="frt-mec-head">
                        <div>
                          <div className="frt-mec-label">{tr("firstRunTourPage.earnedThisMonth")}</div>
                          <div className="frt-mec-total">$1,284</div>
                          <div className="frt-mec-delta">
                            <span className="frt-mec-arrow">▲</span>32%<span className="frt-mec-tail">{tr("firstRunTourPage.vsLastMonth")}</span>
                          </div>
                        </div>
                        <span className="frt-mec-chevron" />
                      </div>
                      <div className="frt-mec-chart-wrap">
                        <svg className="frt-mec-chart" viewBox={`0 0 ${CHART_W} ${CHART_H}`} preserveAspectRatio="none">
                          <path className="frt-mec-line-prev" d={LAST_PATH} vectorEffect="non-scaling-stroke" />
                          <path className="frt-mec-line" d={THIS_PATH} vectorEffect="non-scaling-stroke" />
                        </svg>
                        <span className="frt-mec-head-dot" style={{ left: `${HEAD_X}%`, top: `${HEAD_Y}%` }} />
                      </div>
                      <div className="frt-mec-axis">
                        <span>1</span>
                        <span>8</span>
                        <span>15</span>
                        <span>22</span>
                        <span>31</span>
                      </div>
                    </div>
                  </div>
                  <p className="frt-caption frt-caption--nowrap wt-in wt-d6"><T k="firstRunTourPage.watchItGrowAll" tags={{ 0: (c) => <span className="heading-accent">{c}</span> }} /></p>
                </>
              )}

              {k === "visits" && (
                <>
                  <h2 className="wt-heading wt-in wt-d2">
                    <T k="firstRunTourPage.trackYourFans" tags={{ 0: (c) => <span className="heading-accent">{c}</span> }} />
                  </h2>
                  <p className="wt-sub-line wt-in wt-d3">{tr("firstRunTourPage.profileAndStorefrontVisits")}</p>
                  <div className="frt-mid frt-mid--top">
                    <div className={`frt-pv wt-in wt-d4${pvPhase === "playlist" ? " frt-pv--playlist" : ""}`}>
                      <div className="frt-pv-top">
                        <span className="frt-pv-eye">{EyeIcon}</span>
                        <span className="frt-pv-lbl">{pvPhase === "playlist" ? tr("firstRunTourPage.storefrontVisits") : tr("firstRunTourPage.profileVisits")}</span>
                      </div>
                      <div className="frt-pv-num">{int(pvPhase === "playlist" ? pvPlaylist : pvProfile)}</div>
                      <div className="frt-pv-sub">{tr("firstRunTourPage.lastDays")}</div>
                      <div className="frt-pv-chips" aria-hidden>
                        <span className="frt-pv-chip">24H</span>
                        <span className="frt-pv-chip frt-pv-chip--on">7D</span>
                        <span className="frt-pv-chip">30D</span>
                        <span className="frt-pv-chip">{tr("firstRunTourPage.all")}</span>
                      </div>
                    </div>
                    <div className="frt-board wt-in wt-d5">
                      <div className="frt-board-title">
                        <span>{tr("firstRunTourPage.topFans")}</span>
                        <small>{tr("firstRunTourPage.lastDays2")}</small>
                      </div>
                      {rows(FANS.slice(0, 3))}
                    </div>
                  </div>
                </>
              )}

              {k === "leaderboard" && (
                <>
                  <h2 className="wt-heading wt-in wt-d2">
                    <T k="firstRunTourPage.climbTheLeaderboard" tags={{ 0: (c) => <span className="heading-accent">{c}</span>, 1: () => <span className="frt-fire" aria-hidden><LeaderboardFlame size="0.95em" live /></span> }} />
                  </h2>
                  <p className="wt-sub-line wt-in wt-d3">{tr("firstRunTourPage.topArtistsSongsAnd")}</p>
                  <div className="frt-mid frt-mid--top">
                    <div className="frt-toggle wt-in wt-d4" aria-hidden>
                      {LB_VIEWS.map((v, i) => (
                        <span key={v} className={`frt-toggle-btn${i === lbView ? " frt-toggle-btn--on" : ""}`}>
                          {v}
                        </span>
                      ))}
                    </div>
                    <div className="frt-board wt-in wt-d5">{rows(lbList)}</div>
                  </div>
                </>
              )}

              {k === "refer" && referCode && (
                <>
                  <h2 className="wt-heading wt-in wt-d2">
                    <T k="firstRunTourPage.bringYourFriends" tags={{ 0: (c) => <span className="heading-accent">{c}</span> }} />
                  </h2>
                  <p className="wt-sub-line wt-in wt-d3">{tr("firstRunTourPage.earnOfWhatThey")}</p>
                  <div className="frt-chain wt-in wt-d3" aria-hidden>
                    <div className="frt-chain-av">A</div>
                    <div className="frt-chain-av frt-chain-av--you">{(user?.name || "?")[0].toUpperCase()}</div>
                    <div className="frt-chain-av">J</div>
                    <div className="frt-chain-burst">+2%</div>
                  </div>
                  <div className="frt-mid">
                    <div className="frt-refer-code wt-in wt-d4">
                      <div className="frt-refer-lbl">{tr("firstRunTourPage.yourCode")}</div>
                      <div className="frt-refer-val">{referCode.toUpperCase()}</div>
                      <div className="frt-refer-link"><T k="firstRunTourPage.requestaAppJoin" vars={{ referCode: referCode.toLowerCase() }} tags={{ 0: (c) => <b>{c}</b> }} /></div>
                      <button
                        type="button"
                        className="frt-refer-share"
                        onClick={() => {
                          const text = referralShareText(referCode);
                          if (typeof navigator.share === "function") navigator.share({ text }).catch(() => {});
                          else void copyTextToClipboard(text);
                        }}
                      >
                        {tr("firstRunTourPage.share")}
                      </button>
                    </div>
                  </div>
                  <p className="frt-caption wt-in wt-d5">{tr("firstRunTourPage.findItAnytimeUnder")}</p>
                </>
              )}

              {k === "fivek" && (
                <>
                  <h2 className="wt-heading wt-in wt-d2">
                    <T k="firstRunTourPage.globalPayouts" tags={{ 0: (c) => <span className="heading-accent">{c}</span> }} />
                  </h2>
                  <p className="wt-sub-line wt-in wt-d3">{tr("firstRunTourPage.poweredByStripe")}</p>
                  <div className="frt-5k wt-in wt-d2">
                    <div key={run} className="frt-5k-art">
                      <GlobeCash />
                    </div>
                  </div>
                  <h2 className="wt-heading frt-5k-head wt-in wt-d3">
                    <T k="firstRunTourPage.topArtistsEarnOver" tags={{ 0: <br />, 1: (c) => <span className="heading-accent">{c}</span> }} />
                  </h2>
                  <p className="wt-sub-line frt-5k-sub wt-in wt-d4">{tr("firstRunTourPage.fromRequestsTipsAnd")}</p>
                </>
              )}

              {k === "price" && (
                <>
                  <h2 className="wt-heading wt-in wt-d2">
                    <T k="firstRunTourPage.setYourPrice" tags={{ 0: (c) => <span className="heading-accent">{c}</span> }} />
                  </h2>
                  <p className="wt-sub-line wt-in wt-d3">
                    {tr("firstRunTourPage.inChangeItAnytime", { value: CURRENCY_NAMES[currency] || currency.toUpperCase() })}
                  </p>
                  <div className="frt-mid frt-mid--top">
                    <button
                      type="button"
                      className="psm-price-box frt-price-box wt-in wt-d4"
                      onClick={openPad}
                      aria-label={tr("firstRunTourPage.editPricePerRequest")}
                    >
                      <span className="psm-amount-row">
                        {!suffix && <span className="psm-sym">{sym}</span>}
                        <span className="psm-value">{padOpen ? draft || "0" : String(price)}</span>
                        {suffix && <span className="psm-sym psm-sym-suffix">{sym}</span>}
                      </span>
                      <span className="psm-unit">{tr("firstRunTourPage.perRequest")}</span>
                    </button>
                    <div className="psm-chips frt-chips wt-in wt-d5">
                      {chips.map((p) => (
                        <button
                          key={p}
                          type="button"
                          className={`psm-chip ${!padOpen && price === p ? "active" : ""}`}
                          onClick={() => selectChip(p)}
                        >
                          {suffix ? `${p.toLocaleString()} ${sym}` : `${sym}${p.toLocaleString()}`}
                        </button>
                      ))}
                    </div>
                    {priceError && <p className="psm-error">{priceError}</p>}
                  </div>
                </>
              )}
            </section>
          ))}
        </div>
      </div>

      <div className="wt-footer">
        <button type="button" className="wt-next" onClick={next} disabled={onPriceStep && (saving || !priceCheck.ok)}>
          {onPriceStep ? (saving ? tr("firstRunTourPage.saving") : tr("firstRunTourPage.savePrice")) : lastScreen ? tr("firstRunTourPage.getStarted") : tr("firstRunTourPage.continue")}
        </button>
        {/* The price step has no dots (it's a form, not a screen) — and the
            dots never reach past the tour, since "Get Started" is one-way. */}
        <div className="wt-dots" style={onPriceStep ? { visibility: "hidden" } : undefined}>
          {TOUR.map((k, i) => (
            <button
              key={k}
              type="button"
              className={`wt-dot${i === idx ? " wt-dot--on" : ""}`}
              aria-label={tr("firstRunTourPage.goToPage", { value: i + 1 })}
              onClick={() => !onPriceStep && setIdx(i)}
            />
          ))}
        </div>
      </div>

      {priceStep && (
        <NumberPad
          open={padOpen}
          value={draft}
          onChange={setDraft}
          onDone={closePad}
          maxLength={6}
          note={<PriceFloorNote currency={currency} talent={profile} below={belowFloor(draft, currency, profile)} />}
        />
      )}
    </div>
  );
}
